'use client';

import { useEffect, useRef } from 'react';

export type HeroVariant = 'globe' | 'tree' | 'chain';

type Vec3 = [number, number, number];
const TAU = Math.PI * 2;
const DEG = Math.PI / 180;
const sph = (lat: number, lon: number): Vec3 => [
	Math.cos(lat) * Math.cos(lon),
	Math.sin(lat),
	Math.cos(lat) * Math.sin(lon),
];
// Deterministic LCG (no Math.random — keeps SSR/runtime stable).
const lcg = (seed: number) => () => {
	seed = (seed * 16807) % 2147483647;
	return seed / 2147483647;
};
const hex = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	rad: number,
) => {
	ctx.beginPath();
	for (let s = 0; s < 6; s++) {
		const a = (s / 6) * TAU + Math.PI / 6;
		const hx = x + Math.cos(a) * rad,
			hy = y + Math.sin(a) * rad;
		if (s) ctx.lineTo(hx, hy);
		else ctx.moveTo(hx, hy);
	}
	ctx.closePath();
};

/** Build a draw(t) function for the wireframe-globe variant (KYC). */
function buildGlobe(): (
	ctx: CanvasRenderingContext2D,
	w: number,
	h: number,
	t: number,
) => void {
	const merQ = 24,
		parQ = 13,
		segL = 48,
		segP = 64;
	const mer: Vec3[][] = [];
	for (let m = 0; m < merQ; m++) {
		const lon = (m / merQ) * TAU;
		const line: Vec3[] = [];
		for (let s = 0; s <= segL; s++)
			line.push(sph(-Math.PI / 2 + (s / segL) * Math.PI, lon));
		mer.push(line);
	}
	const par: Vec3[][] = [];
	for (let p = 1; p < parQ; p++) {
		const lat = -Math.PI / 2 + (p / parQ) * Math.PI;
		const line: Vec3[] = [];
		for (let s = 0; s <= segP; s++) line.push(sph(lat, (s / segP) * TAU));
		par.push(line);
	}
	const lines = mer.concat(par);
	const cities: [number, number][] = [
		[40.7, -74],
		[51.5, -0.1],
		[1.35, 103.8],
		[25.2, 55.3],
		[-23.5, -46.6],
		[55.7, 37.6],
		[35.7, 139.7],
		[19.1, 72.9],
		[-33.9, 151.2],
		[6.5, 3.4],
	];
	const pings = cities.map((c, i) => ({
		p: sph(c[0] * DEG, c[1] * DEG),
		ph: (i / cities.length) * TAU,
	}));
	const arcPairs = [
		[0, 1],
		[1, 5],
		[5, 6],
		[3, 2],
		[2, 8],
		[0, 4],
		[6, 8],
	];
	const arcs = arcPairs.map(([ai, bi]) => {
		const A = pings[ai].p,
			B = pings[bi].p,
			line: Vec3[] = [];
		for (let s = 0; s <= 20; s++) {
			const tt = s / 20;
			const x = A[0] + (B[0] - A[0]) * tt,
				y = A[1] + (B[1] - A[1]) * tt,
				z = A[2] + (B[2] - A[2]) * tt;
			const Ln = Math.hypot(x, y, z) || 1,
				r = 1 + 0.13 * Math.sin(tt * Math.PI);
			line.push([(x / Ln) * r, (y / Ln) * r, (z / Ln) * r]);
		}
		return line;
	});

	return (ctx, w, h, t) => {
		const spin = t * 0.18,
			tilt = -0.42;
		const cs = Math.cos(spin),
			ss = Math.sin(spin),
			ct = Math.cos(tilt),
			st = Math.sin(tilt);
		const R = Math.min(w * 0.42, h * 0.46),
			cx = w * 0.54,
			cy = h / 2;
		const proj = (p: Vec3) => {
			const x = p[0] * cs + p[2] * ss,
				z = -p[0] * ss + p[2] * cs,
				y = p[1];
			const y2 = y * ct - z * st,
				z2 = y * st + z * ct;
			return { sx: cx + x * R, sy: cy - y2 * R, d: z2 };
		};
		ctx.lineWidth = 1;
		for (const L of lines)
			for (let s = 0; s < L.length - 1; s++) {
				const a = proj(L[s]),
					b = proj(L[s + 1]);
				const al = 0.05 + Math.max(0, (a.d + b.d) * 0.5 + 0.2) * 0.34;
				if (al < 0.07) continue;
				ctx.strokeStyle = `rgba(242,242,244,${Math.min(0.5, al).toFixed(3)})`;
				ctx.beginPath();
				ctx.moveTo(a.sx, a.sy);
				ctx.lineTo(b.sx, b.sy);
				ctx.stroke();
			}
		for (let ai = 0; ai < arcs.length; ai++) {
			const L = arcs[ai];
			for (let s = 0; s < L.length - 1; s++) {
				const a = proj(L[s]),
					b = proj(L[s + 1]);
				if (a.d < 0 && b.d < 0) continue;
				ctx.strokeStyle = `rgba(242,242,244,${(a.d > 0 ? 0.42 : 0.12).toFixed(3)})`;
				ctx.beginPath();
				ctx.moveTo(a.sx, a.sy);
				ctx.lineTo(b.sx, b.sy);
				ctx.stroke();
			}
			const tt = (t * 0.5 + ai * 0.31) % 1,
				pk = proj(L[Math.floor(tt * (L.length - 1))]);
			if (pk.d > 0) {
				ctx.fillStyle = 'rgba(242,242,244,0.95)';
				ctx.beginPath();
				ctx.arc(pk.sx, pk.sy, 2, 0, TAU);
				ctx.fill();
			}
		}
		for (let i = 0; i < pings.length; i++) {
			const pr = proj(pings[i].p);
			if (pr.d <= 0) continue;
			const pulse = (t * 0.6 + pings[i].ph) % 1;
			ctx.strokeStyle = `rgba(242,242,244,${(0.5 * (1 - pulse)).toFixed(3)})`;
			ctx.lineWidth = 1.2;
			ctx.beginPath();
			ctx.arc(pr.sx, pr.sy, 3 + pulse * 16, 0, TAU);
			ctx.stroke();
			ctx.fillStyle = 'rgba(242,242,244,0.95)';
			ctx.fillRect(pr.sx - 2, pr.sy - 2, 4, 4);
		}
		ctx.strokeStyle = 'rgba(242,242,244,0.12)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.arc(cx, cy, R, 0, TAU);
		ctx.stroke();
	};
}

interface Node {
	x: number;
	y: number;
	z: number;
	kind?: string;
	flag?: boolean;
}

/** Ownership-tree variant (KYB): a root with first-level entities, sub-nodes
 *  and a pulsing UBO node. */
function buildTree() {
	const rnd = lcg(99);
	const nodes: Node[] = [{ x: 0, y: 0, z: 0, kind: 'root' }];
	const edges: [number, number][] = [];
	const N1 = 7;
	for (let i = 0; i < N1; i++) {
		const a = (i / N1) * TAU,
			ph = rnd() - 0.5;
		nodes.push({
			x: Math.cos(a) * 0.82,
			y: ph * 0.7,
			z: Math.sin(a) * 0.82,
			kind: i === 2 ? 'ubo' : 'ent',
		});
		edges.push([0, i + 1]);
	}
	let k = nodes.length;
	for (let i = 1; i <= N1; i++) {
		if (rnd() < 0.6) {
			const base = nodes[i],
				c = 1 + Math.floor(rnd() * 2);
			for (let j = 0; j < c; j++) {
				const a = Math.atan2(base.z, base.x) + (rnd() - 0.5) * 0.9,
					rad = 1.3;
				nodes.push({
					x: Math.cos(a) * rad,
					y: base.y + (rnd() - 0.5) * 0.6,
					z: Math.sin(a) * rad,
					kind: 'sub',
				});
				edges.push([i, k]);
				k++;
			}
		}
	}
	return drawGraph(nodes, edges, {
		spin: 0.2,
		tilt: -0.28,
		rScale: [0.3, 0.34],
	});
}

/** Transaction-graph variant (AML): nodes on a sphere, k-nearest edges, two
 *  flagged (risky) pulsing nodes. */
function buildChain() {
	const rnd = lcg(7);
	const N = 16;
	const nodes: Node[] = [];
	for (let i = 0; i < N; i++) {
		const u = rnd() * TAU,
			v = Math.acos(2 * rnd() - 1),
			r = 0.5 + rnd() * 0.55;
		nodes.push({
			x: r * Math.sin(v) * Math.cos(u),
			y: r * Math.cos(v) * 0.86,
			z: r * Math.sin(v) * Math.sin(u),
			flag: false,
		});
	}
	nodes[3].flag = true;
	nodes[11].flag = true;
	const edges: [number, number][] = [];
	const eset: Record<string, number> = {};
	for (let i = 0; i < N; i++) {
		const d: [number, number][] = [];
		for (let j = 0; j < N; j++) {
			if (i === j) continue;
			const dx = nodes[i].x - nodes[j].x,
				dy = nodes[i].y - nodes[j].y,
				dz = nodes[i].z - nodes[j].z;
			d.push([dx * dx + dy * dy + dz * dz, j]);
		}
		d.sort((a, b) => a[0] - b[0]);
		for (let kk = 0; kk < 3; kk++) {
			const j = d[kk][1],
				key = i < j ? `${i}_${j}` : `${j}_${i}`;
			if (eset[key]) continue;
			eset[key] = 1;
			edges.push([i, j]);
		}
	}
	return drawGraph(nodes, edges, {
		spin: 0.18,
		tilt: -0.2,
		rScale: [0.32, 0.36],
	});
}

/** Shared hexagon-node graph renderer for the tree/chain variants. */
function drawGraph(
	nodes: Node[],
	edges: [number, number][],
	opts: { spin: number; tilt: number; rScale: [number, number] },
) {
	return (ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => {
		const spin = t * opts.spin,
			tilt = opts.tilt;
		const cs = Math.cos(spin),
			ss = Math.sin(spin),
			ct = Math.cos(tilt),
			st = Math.sin(tilt);
		const R = Math.min(w * opts.rScale[0], h * opts.rScale[1]),
			cx = w * 0.54,
			cy = h / 2;
		const proj = (n: Node) => {
			const x = n.x * cs + n.z * ss,
				z = -n.x * ss + n.z * cs,
				y = n.y;
			const y2 = y * ct - z * st,
				z2 = y * st + z * ct;
			const per = 1 / (1 - z2 * 0.18);
			return { sx: cx + x * R * per, sy: cy - y2 * R * per, d: z2 };
		};
		const P = nodes.map(proj);
		for (let e = 0; e < edges.length; e++) {
			const ia = edges[e][0],
				ib = edges[e][1],
				a = P[ia],
				b = P[ib];
			const risk = nodes[ia].flag || nodes[ib].flag;
			const al = 0.07 + Math.max(0, (a.d + b.d) * 0.5 + 0.3) * 0.4;
			ctx.strokeStyle = `rgba(242,242,244,${Math.min(0.55, al).toFixed(3)})`;
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(a.sx, a.sy);
			ctx.lineTo(b.sx, b.sy);
			ctx.stroke();
			const tt = (t * 0.6 + e * 0.19) % 1,
				px = a.sx + (b.sx - a.sx) * tt,
				py = a.sy + (b.sy - a.sy) * tt;
			ctx.fillStyle = `rgba(242,242,244,${(risk ? 0.95 : 0.72).toFixed(3)})`;
			ctx.beginPath();
			ctx.arc(px, py, risk ? 2 : 1.6, 0, TAU);
			ctx.fill();
		}
		for (let i = 0; i < nodes.length; i++) {
			const n = nodes[i],
				pr = P[i],
				front = Math.max(0, pr.d + 0.5);
			const special = n.kind === 'root' || n.kind === 'ubo' || n.flag;
			if (n.kind === 'ubo' || n.flag) {
				const pulse = (t * 0.65 + i) % 1;
				ctx.strokeStyle = `rgba(242,242,244,${(0.52 * (1 - pulse)).toFixed(3)})`;
				ctx.lineWidth = 1.4;
				ctx.beginPath();
				ctx.arc(pr.sx, pr.sy, 6 + pulse * 16, 0, TAU);
				ctx.stroke();
			}
			const rad = n.kind === 'root' ? 7 : special ? 5.5 : 4.5;
			ctx.fillStyle = '#0a0a0b';
			ctx.strokeStyle = `rgba(242,242,244,${Math.min(0.95, 0.4 + front * 0.55 + (special ? 0.18 : 0)).toFixed(3)})`;
			ctx.lineWidth = special ? 1.8 : 1.2;
			hex(ctx, pr.sx, pr.sy, rad);
			ctx.fill();
			ctx.stroke();
			if (n.kind === 'root') {
				ctx.fillStyle = 'rgba(242,242,244,0.95)';
				ctx.fillRect(pr.sx - 2.5, pr.sy - 2.5, 5, 5);
			} else if (n.kind === 'ubo' || n.flag) {
				ctx.fillStyle = 'rgba(242,242,244,0.95)';
				ctx.fillRect(pr.sx - 1, pr.sy - 4, 2, 5);
				ctx.fillRect(pr.sx - 1, pr.sy + 3, 2, 2);
			}
		}
	};
}

/**
 * Hero canvas — a faithful port of the design's hand-coded animations, selected
 * by `variant`: a rotating wireframe globe (KYC), an ownership tree (KYB) or a
 * transaction graph (AML). Pauses for reduced-motion (one static frame) and
 * cleans up its RAF / ResizeObserver on unmount.
 */
export function HeroCanvas({
	variant,
	className,
}: {
	variant: HeroVariant;
	className?: string;
}) {
	const ref = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const ctx = el.getContext('2d');
		if (!ctx) return;

		const draw =
			variant === 'globe'
				? buildGlobe()
				: variant === 'tree'
					? buildTree()
					: buildChain();

		let w = 0,
			h = 0;
		const resize = () => {
			const dpr = Math.min(window.devicePixelRatio || 1, 2);
			w = el.clientWidth;
			h = el.clientHeight;
			el.width = Math.max(1, Math.round(w * dpr));
			el.height = Math.max(1, Math.round(h * dpr));
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};
		resize();
		const ro = new ResizeObserver(resize);
		ro.observe(el);

		const frame = (t: number) => {
			if (!w || !h) return;
			ctx.clearRect(0, 0, w, h);
			draw(ctx, w, h, t);
		};

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			frame(0);
			return () => ro.disconnect();
		}
		let raf = 0;
		const t0 = performance.now();
		const loop = () => {
			frame((performance.now() - t0) / 1000);
			raf = requestAnimationFrame(loop);
		};
		loop();
		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
		};
	}, [variant]);

	return <canvas ref={ref} className={className} aria-hidden="true" />;
}
